// ZZYON LEX — cliente Supabase próprio (projeto zzyon-lex em sa-east-1).
// Separado do torre-shared/http.js que aponta para ZYONCS.
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL =
  import.meta.env?.VITE_LEX_SUPABASE_URL ||
  'https://ywhmlvojowtsfetiyebg.supabase.co';

const SUPABASE_KEY =
  import.meta.env?.VITE_LEX_SUPABASE_KEY ||
  'sb_publishable_9mtwx7N6vCHcKKG7uBtisg_aR5rO5VU';

export const lex = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    persistSession: true,
    storageKey: 'zzy_lex_session',
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
  db: { schema: 'themis' },
});

// Cliente paralelo p/ schemas que NÃO sejam themis (ex: storage/auth)
export const lexAuth = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    persistSession: true,
    storageKey: 'zzy_lex_session',
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

// ─── Helpers de domínio @icaroexpress.com ─────────────────────────────
export function isIcaroEmail(email) {
  if (!email) return false;
  return /@icaroexpress\.com$/i.test(email);
}

export async function getCurrentUser() {
  const { data, error } = await lexAuth.auth.getUser();
  if (error) return null;
  return data?.user || null;
}

export async function signInMagic(email, redirectTo) {
  if (!isIcaroEmail(email)) {
    throw new Error('Apenas e-mails @icaroexpress.com têm acesso à plataforma.');
  }
  const { error } = await lexAuth.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: redirectTo || location.origin },
  });
  if (error) throw error;
}

export async function signOut() {
  await lexAuth.auth.signOut();
}

// ─── Repositories ─────────────────────────────────────────────────────
export async function fetchAreas() {
  const { data, error } = await lex.from('areas').select('*').order('codigo');
  if (error) throw error;
  return data;
}

export async function fetchProcessos({ limit = 50 } = {}) {
  const { data, error } = await lex
    .from('processos')
    .select('id,numero_cnj,area_codigo,tipo,polo,tribunal,vara,comarca,uf,filial_codigo,contraparte,valor_causa,valor_provisao,risco,status,data_distribuicao,ultima_movimentacao_data,ultima_movimentacao_descricao,criado_em')
    .order('ultima_movimentacao_data', { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

export async function fetchProcesso(numero_cnj) {
  const { data, error } = await lex
    .from('processos')
    .select('*')
    .eq('numero_cnj', numero_cnj)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function fetchMovimentacoes(processo_id, { limit = 100 } = {}) {
  const { data, error } = await lex
    .from('movimentacoes')
    .select('id,data,codigo_movimento,nome_movimento,fonte')
    .eq('processo_id', processo_id)
    .order('data', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

export async function fetchAlertas({ limit = 50, naoLidos = false } = {}) {
  let q = lex
    .from('alertas')
    .select('id,ts,tipo,severidade,titulo,mensagem,recurso_tipo,recurso_id,filial_codigo,area_codigo,lido_em,resolvido_em')
    .order('ts', { ascending: false })
    .limit(limit);
  if (naoLidos) q = q.is('lido_em', null);
  const { data, error } = await q;
  if (error) throw error;
  return data;
}

export async function marcarAlertaLido(alertaId, usuarioEmail) {
  const { error } = await lex
    .from('alertas')
    .update({ lido_em: new Date().toISOString(), lido_por: usuarioEmail })
    .eq('id', alertaId);
  if (error) throw error;
}

export async function fetchDossies({ limit = 50 } = {}) {
  const { data, error } = await lex
    .from('vw_dossies_pulso')
    .select('*')
    .limit(limit);
  if (error) throw error;
  return data;
}

export async function fetchLicencasVencendo() {
  const { data, error } = await lex.from('vw_licencas_vencendo').select('*');
  if (error) throw error;
  return data;
}

export async function fetchProvisoesResumo() {
  const { data, error } = await lex.from('vw_provisoes_resumo').select('*');
  if (error) throw error;
  return data;
}

export async function fetchUltimasConsultas({ limit = 10 } = {}) {
  const { data, error } = await lex
    .from('consultas')
    .select('id,criado_em,usuario_email,pergunta,agentes_consultados,duracao_ms,parecer_id')
    .order('criado_em', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

export async function fetchParecer(parecer_id) {
  const { data, error } = await lex
    .from('pareceres')
    .select('*')
    .eq('id', parecer_id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// ─── Pessoas (stakeholders) ─────────────────────────────────────────────
export async function fetchPessoas() {
  const { data, error } = await lex
    .from('pessoas')
    .select('*')
    .eq('ativa', true)
    .order('nome');
  if (error) throw error;
  return data || [];
}

export async function criarPessoa(payload) {
  const { data, error } = await lex.from('pessoas').insert(payload).select().single();
  if (error) throw error;
  return data;
}

// ─── Processo · Pessoas (RACI) ──────────────────────────────────────────
export async function fetchProcessoPessoas(processo_id) {
  const { data, error } = await lex
    .from('processo_pessoas')
    .select('id,responsabilidade,observacao,pessoa:pessoas(id,nome,email,whatsapp,papel,organizacao)')
    .eq('processo_id', processo_id);
  if (error) throw error;
  return data || [];
}

export async function atribuirPessoa({ processo_id, pessoa_id, responsabilidade, observacao = null }) {
  const { error } = await lex.from('processo_pessoas').insert({
    processo_id, pessoa_id, responsabilidade, observacao,
  });
  if (error) throw error;
}

export async function removerPessoaProcesso(id) {
  const { error } = await lex.from('processo_pessoas').delete().eq('id', id);
  if (error) throw error;
}

// ─── Processo · Análise de risco ────────────────────────────────────────
export async function fetchAnaliseAtual(processo_id) {
  const { data, error } = await lex
    .from('processo_analise')
    .select('*')
    .eq('processo_id', processo_id)
    .order('versao', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function salvarAnalise(payload) {
  const ultima = await fetchAnaliseAtual(payload.processo_id);
  const versao = ultima ? (ultima.versao + 1) : 1;
  const { data, error } = await lex
    .from('processo_analise')
    .insert({ ...payload, versao })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ─── Processo · Documentos ──────────────────────────────────────────────
export async function fetchProcessoDocs(processo_id) {
  const { data, error } = await lex
    .from('processo_docs')
    .select('*,responsavel:pessoas(id,nome,email)')
    .eq('processo_id', processo_id)
    .order('ordem');
  if (error) throw error;
  return data || [];
}

export async function criarDoc(payload) {
  const { data, error } = await lex.from('processo_docs').insert(payload).select().single();
  if (error) throw error;
  return data;
}

export async function atualizarDoc(id, patch) {
  const { error } = await lex.from('processo_docs').update(patch).eq('id', id);
  if (error) throw error;
}

export async function removerDoc(id) {
  const { error } = await lex.from('processo_docs').delete().eq('id', id);
  if (error) throw error;
}

// ─── Processo · Plano de ação ───────────────────────────────────────────
export async function fetchProcessoAcoes(processo_id) {
  const { data, error } = await lex
    .from('processo_acoes')
    .select('*,responsavel:pessoas(id,nome,email,whatsapp)')
    .eq('processo_id', processo_id)
    .order('ordem');
  if (error) throw error;
  return data || [];
}

export async function criarAcao(payload) {
  const { data, error } = await lex.from('processo_acoes').insert(payload).select().single();
  if (error) throw error;
  return data;
}

export async function atualizarAcao(id, patch) {
  const { error } = await lex.from('processo_acoes').update(patch).eq('id', id);
  if (error) throw error;
}

export async function concluirAcao(id, resultado, email) {
  const { error } = await lex.from('processo_acoes').update({
    status: 'concluida',
    resultado,
    concluido_em: new Date().toISOString(),
    concluido_por_email: email,
  }).eq('id', id);
  if (error) throw error;
}

export async function removerAcao(id) {
  const { error } = await lex.from('processo_acoes').delete().eq('id', id);
  if (error) throw error;
}
